import { Direction } from '@modules/common/types';
import { AddDashboardContentButton } from '@modules/dashboard/components/Layout/AddDashboardContentButton';
import { DashboardRowCells } from '@modules/dashboard/components/Layout/DashboardRowCells';
import { DashboardRowResizer } from '@modules/dashboard/components/Layout/DashboardRowResizer';
import { RowResizeController } from '@modules/dashboard/components/Layout/helpers';
import {
  getMaxCellsMinHeight,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { DashboardState } from '@modules/dashboard/store/types';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { AddRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { DashboardRow as DashboardRowType } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';

interface DashboardRowProps {
  row: DashboardRowType;
  onResizeEnd: (metadata: DashboardState['metadata']) => void;
  shouldDisableInteraction?: boolean;
}

export function DashboardRow({
  row,
  onResizeEnd,
  shouldDisableInteraction = false,
}: DashboardRowProps) {
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const isUpdateDashboardInProgress = useDashboardStore(
    ({ visual }) => visual.isUpdateDashboardInProgress,
  );

  const rowResizeControllerRef = useRef(
    new RowResizeController({
      row,
      getMaxCellsMinHeight: () => {
        const cellMinimumHeights =
          useDashboardStore.getState().visual.cellMinimumHeightsByRowID[row.id];

        return getMaxCellsMinHeight(cellMinimumHeights);
      },
      onResize: (updatedRow) => {
        requestAnimationFrame(() => {
          const updatedMetaData = cloneDeep(
            useDashboardStore.getState().metadata,
          );
          const rows = updatedMetaData.layout.rows;

          rows.splice(
            rows.findIndex((item) => item.id === row.id),
            1,
            updatedRow,
          );

          dispatch({
            type: DashboardActionType.SET_METADATA,
            payload: { layout: updatedMetaData.layout },
          });
        });
      },
      onResizeEnd: () => {
        trackDashboardEvent({
          eventName: 'Dashboard Row Resize',
        });

        onResizeEnd(useDashboardStore.getState().metadata);
      },
    }),
  );

  useEffect(() => {
    rowResizeControllerRef.current.resetRow(row);
  }, [row]);

  useEffect(() => {
    const rowResizeControllerApi = rowResizeControllerRef.current;

    return () => rowResizeControllerApi.disconnect();
  }, []);

  if (!row) {
    return null;
  }

  return (
    <Box
      className="row"
      sx={{
        position: 'relative',
        '&:hover .add-content-btn': {
          backgroundColor: 'transparent',
          opacity: 1,
        },
      }}
    >
      {shouldDisableInteraction ? null : (
        <AddDashboardContentButton
          rowID={row.id}
          cellInsertDirection={Direction.Left}
          className="add-content-btn"
          sx={{
            minWidth: '40px',
            width: '40px',
            height: 'calc(100% - 20px)',
            position: 'absolute',
            left: -40,
            top: 0,
            mb: '20px',
            mr: '12px',
          }}
        >
          <AddRounded />
        </AddDashboardContentButton>
      )}
      <Box
        className="row-content"
        sx={{ width: '100%', boxSizing: 'border-box' }}
      >
        <DashboardRowCells
          row={row}
          onResizeEnd={onResizeEnd}
          shouldDisableInteraction={
            shouldDisableInteraction || isUpdateDashboardInProgress
          }
        />
        {shouldDisableInteraction ? (
          <Box sx={{ height: '20px', width: '100%' }} />
        ) : (
          <DashboardRowResizer
            isResizable={!isUpdateDashboardInProgress}
            shouldAllowAddContent={!isUpdateDashboardInProgress}
            shouldAllowDrop={!isUpdateDashboardInProgress}
            row={row}
            omMouseDown={
              isUpdateDashboardInProgress
                ? undefined
                : (event) => {
                    rowResizeControllerRef.current.startResizingRowHeight(
                      event.nativeEvent,
                    );
                  }
            }
          />
        )}
      </Box>
      {shouldDisableInteraction ? null : (
        <AddDashboardContentButton
          rowID={row.id}
          cellInsertDirection={Direction.Right}
          className="add-content-btn"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            minWidth: '40px',
            width: '40px',
            height: 'calc(100% - 20px)',
            position: 'absolute',
            right: -40,
            top: 0,
            mb: '20px',
          }}
        >
          <AddRounded />
        </AddDashboardContentButton>
      )}
    </Box>
  );
}
